import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Plus, Camera, Loader2 } from 'lucide-react';
import { ISSUE_STATUS_META, SEVERITY_META, fmtDate } from './fleetUtils';
import IssueDetailDialog from './IssueDetailDialog';

const FreeReportDialog = ({ vehicles, open, onClose, onCreated }) => {
  const [form, setForm] = useState({ vehicle_id: '', item_label: '', description: '', severity: 'defectueux', immobilization_recommended: false });
  const [photoIds, setPhotoIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/fleet/photos`, fd, { withCredentials: true });
      setPhotoIds(p => [...p, res.data.photo_id]);
    } catch (e) {
      alert(e.response?.data?.detail || 'Échec envoi photo');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.vehicle_id || !form.item_label.trim() || !form.description.trim()) {
      alert('Véhicule, élément concerné et description sont obligatoires');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/fleet/issues`, { ...form, photo_ids: photoIds }, { withCredentials: true });
      onCreated?.();
      onClose();
      setForm({ vehicle_id: '', item_label: '', description: '', severity: 'defectueux', immobilization_recommended: false });
      setPhotoIds([]);
    } catch (e) {
      alert(e.response?.data?.detail || 'Signalement refusé');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="free-report-dialog">
        <DialogHeader><DialogTitle>Signalement libre</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Véhicule *</Label>
            <select className="w-full px-3 py-2 border rounded-md bg-white text-sm" value={form.vehicle_id}
              onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="free-report-vehicle">
              <option value="">Choisir un véhicule</option>
              {vehicles.filter(v => v.operational_status !== 'archive').map(v => (
                <option key={v.vehicle_id} value={v.vehicle_id}>{v.plate_number} — {v.brand} {v.model}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Élément concerné *</Label>
            <Input value={form.item_label} onChange={(e) => setForm({ ...form, item_label: e.target.value })}
              placeholder="ex : Voyant moteur, rétroviseur droit..." data-testid="free-report-item" />
          </div>
          <div>
            <Label>Description *</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Décrivez le problème constaté" data-testid="free-report-description" />
          </div>
          <div>
            <Label>Gravité</Label>
            <select className="w-full px-3 py-2 border rounded-md bg-white text-sm" value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="free-report-severity">
              <option value="a_surveiller">À surveiller</option>
              <option value="defectueux">Défectueux</option>
              <option value="bloquant">Bloquant (danger)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.immobilization_recommended}
              onChange={(e) => setForm({ ...form, immobilization_recommended: e.target.checked })}
              data-testid="free-report-immo-reco" />
            Je recommande l'immobilisation de ce véhicule
          </label>
          <div className="flex items-center gap-2">
            <input type="file" accept="image/jpeg,image/png,image/webp" id="free-report-photo" className="hidden"
              onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            <Button type="button" size="sm" variant="outline" disabled={uploading}
              onClick={() => document.getElementById('free-report-photo').click()} data-testid="free-report-photo-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />}
              Ajouter une photo ({photoIds.length})
            </Button>
            {form.severity === 'bloquant' && <span className="text-xs text-red-600">Le chef de parc sera alerté immédiatement</span>}
          </div>
          <Button className="w-full" onClick={submit} disabled={busy} data-testid="free-report-submit">
            {busy ? 'Envoi...' : 'Signaler le problème'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const IssuesTab = ({ vehicles, isFleetManager, onRefreshParc }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [showReport, setShowReport] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = onlyOpen ? '?only_open=true' : '';
      const res = await axios.get(`${API}/fleet/issues${params}`, { withCredentials: true });
      setIssues(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [onlyOpen]);

  const filtered = issues.filter(i => severityFilter === 'all' || i.severity === severityFilter);
  const ageDays = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setShowReport(true)} className="btn-primary" data-testid="open-free-report-btn">
          <Plus className="w-4 h-4 mr-1" /> Signalement libre
        </Button>
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> Ouvertes uniquement
        </label>
        <select className="px-3 py-2 border rounded-md bg-white text-sm" value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}>
          <option value="all">Toutes gravités</option>
          <option value="bloquant">Bloquant</option>
          <option value="defectueux">Défectueux</option>
          <option value="a_surveiller">À surveiller</option>
        </select>
      </div>
      {loading ? <p className="text-gray-500 text-sm">Chargement...</p> : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">
          <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-gray-300" />Aucune anomalie
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((i) => (
            <Card key={i.issue_id} className="cursor-pointer hover:border-blue-300" onClick={() => setDetailId(i.issue_id)}
              data-testid={`issue-row-${i.issue_id}`}>
              <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-mono font-bold">{i.plate_number} <span className="font-sans font-normal text-sm text-gray-600">— {i.item_label}</span></p>
                  <p className="text-xs text-gray-500">{i.reported_by?.name} · {fmtDate(i.created_at)} · il y a {ageDays(i.created_at)} j
                    {i.target_date && ` · prévu le ${i.target_date}`}</p>
                </div>
                <div className="flex gap-2 items-center">
                  {(i.photos || []).length > 0 && <Camera className="w-4 h-4 text-gray-400" />}
                  <span className={`text-xs px-2 py-1 rounded font-bold ${SEVERITY_META[i.severity]?.cls}`}>{SEVERITY_META[i.severity]?.label}</span>
                  <span className={`text-xs px-2 py-1 rounded ${ISSUE_STATUS_META[i.status]?.cls}`}>{ISSUE_STATUS_META[i.status]?.label}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <FreeReportDialog vehicles={vehicles} open={showReport} onClose={() => setShowReport(false)}
        onCreated={() => { load(); onRefreshParc?.(); }} />
      <IssueDetailDialog issueId={detailId} open={!!detailId} onClose={() => setDetailId(null)}
        isFleetManager={isFleetManager} onChanged={() => { load(); onRefreshParc?.(); }} />
    </div>
  );
};

export default IssuesTab;
